import _ from 'lodash';
import { makeObservable, override } from 'mobx';
import {
    CreateLoanProductRequest,
    LoanProductApiFindAllProductsRequest,
    LoanProductDto,
    UpdateLoanProductRequest,
} from 'src/generated-api-client';
import { bpmsResourcesManagerApi, productsApi } from 'src/services/apiServices';
import { ListFilter } from 'src/utils/FormHelper';
import { BasicStore } from 'src/utils/mobx/BasicStore/BasicStore';
import { BasicStoreApi } from 'src/utils/mobx/BasicStore/BasicStore.types';
import { FilterCriteria } from 'src/utils/mobx/FilterCriteria';
import { Pager } from 'src/utils/mobx/Pager';
import { Sorter } from 'src/utils/mobx/Sorter';
import { RequestHelper } from 'src/utils/RequestHelper';

export type ProductsFilter =
    ListFilter<LoanProductApiFindAllProductsRequest> & {
        campaignId?: string | number; // from url params
    };

export class ProductsStoreClass extends BasicStore<
    LoanProductDto,
    ProductsFilter,
    UpdateLoanProductRequest,
    CreateLoanProductRequest,
    LoanProductDto
> {
    constructor() {
        super();
        makeObservable(this);
    }

    itemDetailsFormKey = 'embedded:deployment:forms/bo/bo-14-product.json';

    itemEditFormKey = 'embedded:deployment:forms/bo/bo-16-edit-product.json';

    itemCreateFormKey =
        'embedded:deployment:forms/bo/bo-15-create-product.json';

    pager = new Pager();
    sorter = new Sorter();
    filterCriteria: FilterCriteria<ProductsFilter> = new FilterCriteria();

    api: BasicStoreApi<
        LoanProductDto,
        UpdateLoanProductRequest,
        CreateLoanProductRequest,
        LoanProductDto
    > = {
        loadList: async (...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);
            const resp = await RequestHelper.unwrapFromAxiosPromise(
                productsApi.findAllProducts(
                    {
                        ...this.pager,
                        ...this.sorter,
                        ...this.filterCriteria.filter,
                        filterCampaignId: this.filterCriteria.filter.campaignId // from url params
                            ? String(this.filterCriteria.filter.campaignId)
                            : undefined,
                    },
                    options,
                ),
            );

            this.pager.setTotal(resp.totalElements || 0);

            return resp.content || [];
        },

        loadItemForm: (formKey: string, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                bpmsResourcesManagerApi.getForm({ formKey }, options),
            );
        },
        loadItem: (id: number, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                productsApi.getProduct({ id }, options),
            );
        },
        updateItem: (id, data, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                productsApi.updateProduct(
                    { id: Number(id), updateLoanProductRequest: data },
                    options,
                ),
            );
        },
        createItem: (data, ...args) => {
            const options = RequestHelper.getOptionsFromArgs(args);

            return RequestHelper.unwrapFromAxiosPromise(
                productsApi.createProduct(
                    { createLoanProductRequest: data },
                    options,
                ),
            );
        },
    };

    @override get hasFilter() {
        const { filterActive, filterCampaignId, filterSearch } =
            this.filterCriteria.filter;

        return (
            !_.isNil(filterActive) ||
            !_.isNil(filterCampaignId) ||
            !_.isNil(filterSearch)
        );
    }
}

export const ProductsStore = new ProductsStoreClass();
